import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { PATHS } from '@/app/router/routes'
import { HistoricalPropertyBadge } from '@/features/negotiations/components/cells/cell-negotiation-property'
import type { DocumentApprovalFlowResponse } from '@/features/negotiations/hooks/use-document-approval-flows'
import { useNegotiationsByClientV2Query } from '@/features/negotiations/hooks/use-negotiations-admin-v2-query'
import { daysSince } from '@/features/negotiations/models/negotiation-dates'
import { getFlowsByNegotiation } from '@/features/negotiations/services/approval-flow.services'
import type { NegotiationsAdminV2Row } from '@/features/negotiations/services/negotiations-admin-v2.service'
import { resolveNegotiationProperty } from '@/features/negotiations/utils/resolve-negotiation-property'
import { QueryErrorState } from '@/shared/components/query-error-state/query-error-state'
import { formatCode } from '@/shared/constants/config'
import { queryKeys } from '@/shared/constants/query-keys'
import { cn } from '@/shared/lib/utils'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar'
import { Skeleton } from '@/shared/ui/skeleton'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/ui/tooltip'
import { resolveListView } from '@/shared/utils/resolve-list-view'

type ClientNegotiationsTabProps = {
  clientId: string
}

/**
 * Una negociación del cliente, leída del listado v2.
 *
 * El `neg_` viaja como `id` —v2 no publica el `_id`— así que la tarjeta ya no necesita
 * `negotiationRouteId`: no hay forma alterna que resolver. Ese helper existe para las pantallas
 * que todavía reciben filas de v1 con `publicId` opcional.
 */
function NegotiationCard({ negotiation }: { negotiation: NegotiationsAdminV2Row }) {
  const navigate = useNavigate()
  const state = negotiation.currentState?.state
  // `daysSince` devuelve `null` —no `NaN`— cuando la fecha no se puede leer. Acá se imprimía
  // «Duración: NaN día(s)»: `stateAt` es un string y `new Date()` sobre algo que no es fecha da
  // `Invalid Date`, cuya resta es `NaN`, y la guarda de abajo era `days != null`.
  const days = daysSince(negotiation.currentState?.stateAt)
  const propertyDisplay = resolveNegotiationProperty(
    negotiation.property,
    negotiation.previousProperty
  )
  const { data: flows = [] } = useQuery<DocumentApprovalFlowResponse[]>({
    queryKey: queryKeys.approvalFlows.detail(negotiation.id, 'negotiation-preview'),
    // Sin `try/catch`: devolver `[]` le decía a React Query que había salido bien, así que el
    // fallo no existía para nadie —ni para un reintento, ni para las devtools— y la tira de
    // participantes desaparecía sin dejar rastro. La tarjeta no AFIRMA que no hay involucrados
    // (a diferencia del historial de aprobación, que sí lo dice con todas las letras), pero un
    // fallo tiene que ser un fallo: acá se omite la tira y la query queda en error, no en éxito.
    // El `neg_`, no el `_id`: desde la decisión Nº 16 el endpoint de flujos traduce el id
    // público, así que esta tarjeta dejó de necesitar el interno.
    //
    // ⚠️ Y con eso se fue el `enabled: !!id` que había acá: en v1 el `publicId` era opcional y
    // valía la pena no disparar la query con un id vacío. En v2 el `id` es OBLIGATORIO en la fila
    // —el tipo lo garantiza—, así que la guarda no podía fallar y sólo escondía que la identidad
    // ya no es opcional. Un `enabled` que nunca es falso se lee como una protección que existe.
    queryFn: () => getFlowsByNegotiation(negotiation.id),
    refetchOnReconnect: true,
    refetchOnWindowFocus: false
  })
  const latestFlow = [...flows].sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  )[0]

  return (
    <button
      type="button"
      onClick={() => {
        navigate(PATHS.admin.negotiations.view(negotiation.id))
      }}
      className="flex w-full flex-col gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent/40"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold">{formatCode(negotiation.code, 'negotiation')}</p>
          <div className="flex min-w-0 items-center gap-1.5">
            <p
              className={cn(
                'min-w-0 truncate text-sm text-muted-foreground',
                propertyDisplay.isHistorical && 'line-through decoration-1'
              )}
              title={propertyDisplay.label}
            >
              {propertyDisplay.label}
            </p>
            {propertyDisplay.isHistorical ? <HistoricalPropertyBadge /> : null}
          </div>
          {negotiation.project?.name && (
            <p className="text-xs text-muted-foreground">{negotiation.project.name}</p>
          )}
        </div>
        {state?.name && (
          <span
            className="shrink-0 rounded px-1.5 py-0.5 text-xs font-medium"
            style={
              state.color ? { backgroundColor: `${state.color}1a`, color: state.color } : undefined
            }
          >
            {state.name}
          </span>
        )}
      </div>

      {latestFlow?.participants?.length ? (
        <div className="flex flex-col gap-1.5">
          <TooltipProvider>
            <div className="*:data-[slot=avatar]:ring-background flex -space-x-1.5 *:data-[slot=avatar]:ring-2">
              {latestFlow.participants.map((participant, index) => (
                <Tooltip delayDuration={0} key={participant.id}>
                  <TooltipTrigger asChild>
                    <div className="relative">
                      <Avatar
                        seed={participant.id}
                        className="h-8 w-8 select-none"
                        style={{ zIndex: latestFlow.participants.length - index }}
                      >
                        <AvatarImage
                          src={participant.avatar ?? undefined}
                          className="object-cover"
                        />
                        <AvatarFallback className="bg-neutral-400 uppercase select-none">
                          {participant.name?.[0] ?? participant.email?.[0] ?? '?'}
                        </AvatarFallback>
                      </Avatar>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent className="flex flex-col gap-0.5">
                    {participant.name && participant.name !== participant.email ? (
                      <p className="max-w-[20ch] text-xs font-semibold leading-tight text-primary">
                        {participant.name}
                      </p>
                    ) : null}
                    <p className="text-xs leading-tight text-muted-foreground">
                      {participant.email}
                    </p>
                  </TooltipContent>
                </Tooltip>
              ))}
            </div>
          </TooltipProvider>
          <p className="text-xs text-muted-foreground">
            {latestFlow.participants.length} involucrado(s)
          </p>
        </div>
      ) : null}

      {days != null && <p className="text-xs text-muted-foreground">Duración: {days} día(s)</p>}
    </button>
  )
}

/** "Negociaciones" tab: the client's negotiations, most recent first. */
export function ClientNegotiationsTab({ clientId }: ClientNegotiationsTabProps) {
  const { data, status, error, refetch } = useNegotiationsByClientV2Query({
    clientId,
    enabled: !!clientId
  })
  const negotiations = data?.data ?? []
  const view = resolveListView({ status, count: negotiations.length })

  // Esta pestaña lee v2: el listado con `filters[ownerId]`, igual que las dos del FEIC. Lo último
  // que la ataba a v1 era el cruce con los flujos de aprobación por `_id`, y eso lo destrabó la
  // decisión Nº 16 — el endpoint ya traduce el `neg_`.
  //
  // La rama de error no depende de nada de eso: hasta que se escribió, un fallo decía «este cliente
  // no tiene negociaciones registradas», que no es lo que pasó.
  if (view === 'error') {
    return <QueryErrorState error={error} resource="las negociaciones" onRetry={() => refetch()} />
  }

  if (view === 'loading') {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    )
  }

  if (view === 'empty') {
    return (
      <p className="text-sm text-muted-foreground">
        Este cliente no tiene negociaciones registradas.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      {negotiations.map((negotiation) => (
        <NegotiationCard key={negotiation.id} negotiation={negotiation} />
      ))}
    </div>
  )
}
